import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, TrendingUp, Handshake, Sparkles, Building2, BarChart3, Lock, Phone, Flame, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PropertyCard from "@/components/PropertyCard";
import JVCard from "@/components/JVCard";
import MarketplacePulse from "@/components/MarketplacePulse";
import DealPulse from "@/components/DealPulse";
import DealWins from "@/components/DealWins";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";
import BulaBriefSubscribe from "@/components/BulaBriefSubscribe";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=1920&q=85";

export default function Home() {
    const [listings, setListings] = useState([]);
    const [jvs, setJvs] = useState([]);
    const [trending, setTrending] = useState([]);

    useEffect(() => {
        api.get("/listings?limit=4").then(({ data }) => setListings(data.items || []));
        api.get("/jv").then(({ data }) => setJvs((data.items || []).slice(0, 3)));
        api.get("/jv/trending?limit=6").then(({ data }) => setTrending(data.items || []));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[88vh] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${HERO_BG}')` }}
                />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 bg-ocean-deep/30" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
                    <div className="max-w-3xl text-white animate-fade-up">
                        <Badge className="bg-coral/95 text-white border-0 mb-6 uppercase tracking-[0.25em] text-[10px] font-app font-bold px-4 py-1.5">
                            Fiji's #1 Land Joint Venture Marketplace
                        </Badge>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] font-bold tracking-tight mb-6">
                            Don't just sell your land.<br />
                            <span className="text-turquoise italic">Own a piece of what it becomes.</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-white/90 max-w-2xl font-app leading-relaxed mb-8">
                            Fiji's premier real estate & joint venture ecosystem — built so landowners earn far more than just a sale price by partnering with developers and investors.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Button asChild size="lg" data-testid="hero-list-land-btn" className="bg-coral hover:bg-coral-deep text-white font-app text-base px-8 h-12 shadow-xl">
                                <Link to="/jv/submit">
                                    List Your Land for Joint Venture <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" data-testid="hero-explore-btn" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 font-app text-base px-8 h-12">
                                <Link to="/marketplace">Explore Marketplace</Link>
                            </Button>
                        </div>

                        <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm font-app text-white/80">
                            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-turquoise" /> Platform-as-Intermediary</div>
                            <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-turquoise" /> Vetted Joint Venture Deals</div>
                            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-turquoise" /> Phone-Verified Members</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARKETPLACE PULSE — live social-proof strip */}
            <MarketplacePulse />

            {/* RECENT WINS TICKER — anonymized closed/paid deals (auto-hides if none) */}
            <DealWins />

            {/* DEAL PIPELINE PULSE — auto-hides when no deals exist yet */}
            <DealPulse />

            {/* REFERRAL LEADERBOARD — auto-hides when no paid referral earnings yet */}
            <ReferralLeaderboard />

            {/* TRENDING DEALS RAIL */}
            {trending.length > 0 && (
                <section className="py-16 lg:py-20 border-b border-border" data-testid="trending-rail-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Flame className="h-4 w-4 text-coral" />
                                    <div className="overline text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold">Trending this week</div>
                                </div>
                                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                                    The deals investors are watching <span className="italic text-turquoise">right now</span>.
                                </h2>
                            </div>
                            <Button asChild variant="outline" data-testid="view-all-trending-btn" className="font-app">
                                <Link to="/jv">All deals <ArrowRight className="ml-2 h-4 w-4" /></Link>
                            </Button>
                        </div>
                        <div className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory">
                            {trending.map((jv) => (
                                <div key={jv.id} className="snap-start flex-shrink-0 w-[300px] sm:w-[340px]">
                                    <JVCard jv={jv} />
                                    {jv.recent_favorites > 0 && (
                                        <div className="mt-2 text-xs font-app text-coral flex items-center gap-1">
                                            <Star className="h-3 w-3 fill-coral" /> {jv.recent_favorites} new {jv.recent_favorites === 1 ? "favorite" : "favorites"} this week
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* THE Joint Venture PROBLEM/SOLUTION */}
            <section className="py-20 lg:py-28 bg-sand/30 dark:bg-secondary/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        <div className="animate-fade-up">
                            <div className="overline text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-4">For Landowners</div>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                                Tired of selling your land for less than it's worth?
                            </h2>
                            <p className="text-base sm:text-lg text-muted-foreground font-app leading-relaxed mb-6">
                                Most Fijian landowners struggle to find buyers willing to pay what their land is truly worth. Selling outright means walking away from the upside — the resort, the subdivision, the developed asset that follows.
                            </p>
                            <p className="text-base sm:text-lg text-muted-foreground font-app leading-relaxed mb-8">
                                A <strong className="text-ocean dark:text-turquoise">Joint Venture</strong> changes that. Instead of just selling, you contribute your land as equity into the project — and earn a share of the developed value when the buyer cashes out. You earn your asking price <em>and</em> a slice of the upside.
                            </p>
                            <Button asChild size="lg" data-testid="learn-jv-btn" className="bg-ocean-green text-white hover:opacity-90 font-app">
                                <Link to="/how-it-works">Learn how Joint Ventures work <ArrowRight className="ml-2 h-4 w-4" /></Link>
                            </Button>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            {[
                                { icon: Handshake, title: "Equity Partnership", desc: "Convert your land into 18–25% project equity instead of a one-time sale.", color: "text-coral" },
                                { icon: TrendingUp, title: "Earn the Upside", desc: "Cash out alongside the developer — typically 2–4× your land's market value.", color: "text-fiji-green" },
                                { icon: ShieldCheck, title: "Platform-Vetted", desc: "Every Joint Venture is reviewed by our deal team before any investor sees it.", color: "text-ocean dark:text-turquoise" },
                                { icon: Sparkles, title: "We Stay In The Deal", desc: "Bula Real Estate Fiji is the official intermediary — protecting your interests throughout.", color: "text-coral" },
                            ].map((item) => (
                                <div key={item.title} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                                    <div className={`h-10 w-10 rounded-md bg-secondary grid place-items-center mb-4 ${item.color}`}>
                                        <item.icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold mb-1.5">{item.title}</h3>
                                    <p className="text-sm text-muted-foreground font-app leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED Joint Ventures */}
            <section className="py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <div className="overline text-xs uppercase tracking-[0.3em] text-turquoise font-app font-semibold mb-2">Vetted Opportunities</div>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                                Live Joint Venture<br /> deals in <span className="text-gradient-ocean">Fiji</span>.
                            </h2>
                        </div>
                        <Button asChild variant="outline" data-testid="view-all-jvs-btn" className="font-app">
                            <Link to="/jv">View all Joint Ventures <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>

                    {jvs.length > 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {jvs.map((jv) => <JVCard key={jv.id} jv={jv} />)}
                        </div>
                    ) : (
                        <div className="text-center py-16 text-muted-foreground font-app">No approved Joint Ventures yet — be the first to submit.</div>
                    )}
                </div>
            </section>

            {/* MARKETPLACE PREVIEW */}
            <section className="py-20 lg:py-28 bg-secondary/40 dark:bg-card/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <div className="overline text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-2">Marketplace</div>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                                Hand-picked land<br /> across the islands.
                            </h2>
                        </div>
                        <Button asChild variant="outline" data-testid="view-all-listings-btn" className="font-app">
                            <Link to="/marketplace">Browse all <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
                    </div>
                </div>
            </section>

            {/* TRUST / METRICS */}
            <section className="py-20 lg:py-24 bg-ocean-deep-grad text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8 text-center">
                        {[
                            { stat: "100%", label: "Platform-routed deals" },
                            { stat: "30+", label: "Photos per listing" },
                            { stat: "4", label: "Major Fiji island groups" },
                            { stat: "18-25%", label: "Typical landowner equity" },
                        ].map((m) => (
                            <div key={m.label}>
                                <div className="font-display text-5xl lg:text-6xl font-bold text-turquoise">{m.stat}</div>
                                <div className="mt-2 text-sm font-app text-white/80 uppercase tracking-wider">{m.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BULA BRIEF SUBSCRIBE */}
            <BulaBriefSubscribe />

            {/* FINAL CTA */}
            <section className="py-24 bg-background">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Building2 className="h-12 w-12 mx-auto text-coral mb-6" />
                    <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
                        Ready to unlock your land's<br />
                        <span className="text-gradient-ocean">true potential</span>?
                    </h2>
                    <p className="text-lg text-muted-foreground font-app mb-8 max-w-2xl mx-auto">
                        Join landowners, agents, and global investors building the next generation of Fijian real estate together.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button asChild size="lg" data-testid="cta-register-btn" className="bg-ocean-green text-white hover:opacity-90 font-app text-base px-8 h-12">
                            <Link to="/register">Create your free account</Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" data-testid="cta-jv-btn" className="font-app text-base px-8 h-12">
                            <Link to="/jv/submit">Submit a Joint Venture proposal</Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
